import { KitButton } from './button';
import { useState } from 'react';
import { TableCheck, TableDesktopRow, TablePhoneRow } from './table-rows';
import { usePhoneTable, useTableWindow } from './table-window';
import type { DataTableProps } from './table-types';
export type { DataTableProps, TableColumn, TableRow } from './table-types';

export function DataTable(props: DataTableProps) {
  const { rows, columns, density = 'comfortable', state = rows.length ? 'ready' : 'empty' } = props;
  const phone = usePhoneTable();
  const { scrollRef, headRef, range } = useTableWindow(rows.length, density);
  const [phonePage, setPhonePage] = useState(0);
  const pageSize = Math.min(50, Math.max(1, Math.trunc(props.phonePageSize || 10)));
  const page = Math.min(phonePage, Math.max(0, Math.ceil(rows.length / pageSize) - 1));
  const pageStart = page * pageSize;
  const selected = new Set(props.selectedRowIds ?? []);
  const visible = phone
    ? rows.slice(pageStart, pageStart + pageSize)
    : rows.slice(range.start, range.end);
  const selectable = visible.filter((row) => props.canSelect?.(row) ?? true);
  const chosen = selectable.filter((row) => selected.has(row.id)).length;
  const selectVisible = (value: boolean) => {
    const result = new Set(selected);
    for (const row of selectable) {
      if (value) result.add(row.id);
      else result.delete(row.id);
    }
    props.onSelectionChange?.([...result]);
  };
  const toggle = (id: string, value: boolean) => {
    const result = new Set(selected);
    if (value) result.add(id);
    else result.delete(id);
    props.onSelectionChange?.([...result]);
  };
  return (
    <div
      className="pc-dt"
      data-density={density}
      data-state={state === 'ready' ? undefined : state}
    >
      <div className="pc-dt-bar">
        {selected.size ? props.bulkToolbar : props.toolbar}
        {phone && props.onToggleSelectMode && (
          <KitButton type="button" onClick={props.onToggleSelectMode}>
            {props.selectMode ? props.copy.done : props.copy.select}
          </KitButton>
        )}
        {phone && props.onOpenSortAndFilter && (
          <KitButton type="button" onClick={props.onOpenSortAndFilter}>
            {props.copy.sortAndFilter}
          </KitButton>
        )}
      </div>
      {state === 'error' && (
        <div className="pc-notice" data-tone="danger" role="alert">
          {props.error}
        </div>
      )}
      <div
        className="pc-dt-scroll"
        ref={scrollRef}
        role={phone ? undefined : 'grid'}
        aria-rowcount={phone ? undefined : rows.length + 1}
        aria-colcount={phone ? undefined : columns.length + (props.onSelectionChange ? 1 : 0)}
        aria-busy={state === 'loading'}
      >
        {!phone && (
          <div
            className="pc-dt-head"
            ref={headRef}
            role="row"
            style={{
              gridTemplateColumns: `${props.onSelectionChange ? 'max-content ' : ''}repeat(${columns.length}, minmax(0, 1fr))`,
            }}
          >
            {props.onSelectionChange && (
              <div className="pc-dt-select" role="columnheader">
                <TableCheck
                  checked={!!selectable.length && chosen === selectable.length}
                  mixed={chosen > 0 && chosen < selectable.length}
                  label={props.copy.selectVisible}
                  onChange={selectVisible}
                />
              </div>
            )}
            {columns.map((column) => (
              <div
                key={column.id}
                className="pc-dt-th"
                role="columnheader"
                aria-sort={props.sort?.columnId === column.id ? props.sort.direction : 'none'}
                data-align={['number', 'money', 'date'].includes(column.type) ? 'end' : 'start'}
              >
                {props.onSort ? (
                  <KitButton
                    variant="quiet"
                    type="button"
                    aria-label={props.copy.sort(column)}
                    onClick={() => props.onSort?.(column.id)}
                  >
                    {column.label}
                    {props.sort?.columnId === column.id && (
                      <span aria-hidden="true">
                        {props.sort.direction === 'ascending' ? '▲' : '▼'}
                      </span>
                    )}
                  </KitButton>
                ) : (
                  column.label
                )}
              </div>
            ))}
          </div>
        )}
        {state === 'loading' ? (
          <div className="pc-dt-body" aria-hidden="true">
            {Array.from({ length: 8 }, (_, index) => (
              <div className="pc-dt-row" data-state="saving" key={index} />
            ))}
          </div>
        ) : !rows.length ? (
          props.empty
        ) : phone ? (
          <div className="pc-dt-body">
            {visible.map((row) => (
              <TablePhoneRow
                key={row.id}
                row={row}
                props={props}
                selected={selected.has(row.id)}
                toggle={(value) => toggle(row.id, value)}
              />
            ))}
          </div>
        ) : (
          <div
            className="pc-dt-body"
            style={{
              paddingTop: range.offsetTop,
              paddingBottom: Math.max(
                0,
                range.totalHeight - range.end * (range.totalHeight / Math.max(1, rows.length)),
              ),
            }}
          >
            {visible.map((row, index) => (
              <TableDesktopRow
                key={row.id}
                row={row}
                index={range.start + index}
                props={props}
                selected={selected.has(row.id)}
                toggle={(value) => toggle(row.id, value)}
              />
            ))}
          </div>
        )}
      </div>
      {phone && rows.length > pageSize && state !== 'loading' && (
        <div className="pc-dt-pager">
          <KitButton disabled={page === 0} onClick={() => setPhonePage(page - 1)}>
            {props.copy.previousPage}
          </KitButton>
          <span role="status">
            {props.copy.page(
              pageStart + 1,
              Math.min(pageStart + pageSize, rows.length),
              rows.length,
            )}
          </span>
          <KitButton
            disabled={pageStart + pageSize >= rows.length}
            onClick={() => setPhonePage(page + 1)}
          >
            {props.copy.nextPage}
          </KitButton>
        </div>
      )}
    </div>
  );
}
